/**
 * RoleModal - alta/edicion de un rol: identidad y matriz de permisos.
 *
 * Usa el Modal compartido de la plataforma y las mismas convenciones de
 * formulario que UserForm: labels `text-white-50`, controles
 * `form-control bg-dark text-white border-secondary py-2` y errores en
 * `small text-danger` (no `invalid-feedback`, que no se ve sobre fondo oscuro).
 *
 * La matriz se mantiene agrupada por modulo, pero dentro de cada modulo los
 * permisos se eligen de un combo y se acumulan como tags quitables, no como una
 * casilla por accion.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../ModalConversion';
import { countOn, granted, label } from './roleMatrix';
import { PermissionPicker } from './PermissionPicker';

export function RoleModal({
    isOpen,
    onClose,
    isNew,
    code,
    name,
    description,
    draft,
    fieldErrors,
    catalog,
    totalActions,
    canSave,
    saving,
    onChange,
    onSetAction,
    onSetModule,
    onSave,
}) {
    const enabled = countOn(draft, catalog);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <h4 className="text-white mb-1">{isNew ? 'Nuevo rol' : `Editar ${code}`}</h4>
            <p className="text-white-50 mb-4" style={{ fontSize: '0.85rem' }}>
                {isNew
                    ? 'Define la identidad del rol y que puede hacer. El codigo no se puede cambiar despues.'
                    : 'Los cambios aplican a quien tenga este rol y no tenga permisos propios.'}
            </p>

            <div className="text-white-50 small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                Identidad del rol
            </div>
            <div className="row g-3 mb-2">
                <div className="col-md-4">
                    <label className="form-label text-white-50">Codigo *</label>
                    <input
                        type="text"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={code}
                        disabled={!isNew || saving}
                        placeholder="editor_jr"
                        onChange={(e) => onChange('code', e.target.value)}
                    />
                    {fieldErrors.code
                        ? <small className="text-danger d-block mt-1">{fieldErrors.code}</small>
                        : <small className="text-white-50 d-block mt-1">Minusculas, digitos y guion bajo.</small>}
                </div>
                <div className="col-md-4">
                    <label className="form-label text-white-50">Nombre *</label>
                    <input
                        type="text"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={name}
                        disabled={saving}
                        placeholder="Editor Junior"
                        onChange={(e) => onChange('name', e.target.value)}
                    />
                    {fieldErrors.name && <small className="text-danger d-block mt-1">{fieldErrors.name}</small>}
                </div>
                <div className="col-md-4">
                    <label className="form-label text-white-50">Descripcion</label>
                    <input
                        type="text"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={description}
                        disabled={saving}
                        placeholder="Opcional"
                        onChange={(e) => onChange('description', e.target.value)}
                    />
                </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                <div className="text-white-50 small fw-bold text-uppercase" style={{ letterSpacing: '0.05em' }}>
                    Permisos por modulo
                </div>
                <span className="text-white-50" style={{ fontSize: '0.85rem' }}>
                    {enabled} de {totalActions} acciones
                </span>
            </div>

            <div className="modern-table-container">
                <table className="modern-table striped-table">
                    <thead>
                        <tr>
                            <th>Modulo</th>
                            <th>Permisos del rol</th>
                        </tr>
                    </thead>
                    <tbody>
                        {catalog.map((m) => {
                            const from = draft[m.code] || {};
                            const moduleOn = m.actions.filter((a) => granted(from[a.code])).length;
                            return (
                                <tr key={m.code}>
                                    <td className="short align-top">
                                        <div className="fw-bold text-white">{label(m)}</div>
                                        <div className="text-white-50" style={{ fontSize: '0.75rem' }}>
                                            {m.code} · {moduleOn}/{m.actions.length}
                                        </div>
                                    </td>
                                    <td className="short align-top">
                                        <PermissionPicker
                                            mod={m}
                                            draft={draft}
                                            disabled={saving || !canSave}
                                            onSetAction={onSetAction}
                                            onSetModule={onSetModule}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
                <button type="button" className="btn btn-outline-secondary px-4" onClick={onClose} disabled={saving}>
                    Cancelar
                </button>
                <button
                    type="button"
                    className="btn btn-primary px-4 shadow-sm d-flex align-items-center gap-2"
                    onClick={onSave}
                    disabled={saving || !canSave}
                >
                    {saving && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {isNew ? 'Crear rol' : 'Guardar cambios'}
                </button>
            </div>
        </Modal>
    );
}

export default RoleModal;
